import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Container,
  Typography,
  Paper,
  TextField,
  CircularProgress,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
  Snackbar,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from '@mui/material';
import { 
  Save as SaveIcon, 
  ExpandMore as ExpandMoreIcon,
  History as HistoryIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import api from '../../utils/axios';

const AdminButtConfig = () => {
  const [config, setConfig] = useState(null);
  const [configName, setConfigName] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showHistory, setShowHistory] = useState(false);
  const [configHistory, setConfigHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [openNewVersionDialog, setOpenNewVersionDialog] = useState(false);
  const [newVersionName, setNewVersionName] = useState('');

  const fetchCurrentConfig = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await api.get('/admin/butt-config');
      setConfig(response.data.mainConfig);
      setConfigName(response.data.name);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to fetch BUTT configuration.');
    } finally {
      setLoading(false);
    }
  };

  const fetchConfigHistory = async () => {
    try {
      setHistoryLoading(true);
      const response = await api.get('/admin/butt-config/history');
      setConfigHistory(response.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to fetch configuration history.');
    } finally {
      setHistoryLoading(false);
    }
  };

  useEffect(() => {
    fetchCurrentConfig();
  }, []);

  const handleToggleHistory = () => {
    setShowHistory(!showHistory);
    if (!showHistory && configHistory.length === 0) {
      fetchConfigHistory();
    }
  };

  const handleSaveConfig = async () => {
    if (!config || !configName) {
      setError('Configuration content and name are required.');
      return;
    }

    try {
      setSaving(true);
      setError('');
      await api.put('/admin/butt-config', {
        mainConfig: config,
        name: configName,
      });
      setSuccess('BUTT configuration saved successfully.');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save configuration.');
    } finally {
      setSaving(false);
    }
  };

  const handleCreateNewVersion = async () => {
    if (!config || !newVersionName) {
      setError('Configuration content and name are required.');
      return;
    }

    try {
      setSaving(true);
      setError('');
      await api.post('/admin/butt-config/versions', {
        mainConfig: config,
        name: newVersionName,
      });
      setSuccess('New BUTT configuration version created successfully.');
      setTimeout(() => setSuccess(''), 3000);
      setOpenNewVersionDialog(false);
      setNewVersionName('');
      // Refresh history if it's shown
      if (showHistory) {
        fetchConfigHistory();
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create new version.');
    } finally {
      setSaving(false);
    }
  };

  const handleLoadVersion = (version) => {
    setConfig(version.mainConfig);
    setConfigName(version.name + ' (Copy)');
    setShowHistory(false);
    window.scrollTo(0, 0);
  };

  const handleCloseNewVersionDialog = () => {
    setOpenNewVersionDialog(false);
    setNewVersionName('');
  };

  return (
    <Container maxWidth="lg">
      <Box sx={{ my: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h4" component="h1">
            Admin: BUTT Configuration Template
          </Typography>
          <Box>
            <Button 
              sx={{ mr: 1 }}
              variant="outlined" 
              startIcon={<RefreshIcon />}
              onClick={fetchCurrentConfig}
              disabled={loading}
            >
              Refresh
            </Button>
            <Button 
              sx={{ mr: 1 }}
              variant="outlined" 
              startIcon={<HistoryIcon />}
              onClick={handleToggleHistory}
              disabled={historyLoading}
            >
              {showHistory ? 'Hide History' : 'Show History'}
            </Button>
            <Button 
              variant="contained" 
              color="secondary"
              onClick={() => setOpenNewVersionDialog(true)}
              disabled={saving || !config}
              sx={{ mr: 1 }}
            >
              Create New Version
            </Button>
            <Button 
              variant="contained" 
              startIcon={<SaveIcon />} 
              onClick={handleSaveConfig}
              disabled={saving || !config}
            >
              Save Changes
            </Button>
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Snackbar
          open={!!success}
          autoHideDuration={3000}
          onClose={() => setSuccess('')}
          message={success}
        />

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 5 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Paper elevation={3} sx={{ p: 3, mb: 4 }}>
            <Typography variant="h6" component="h2" gutterBottom>
              Current Configuration
            </Typography>
            <Typography variant="body2" sx={{ mb: 2 }}>
              This template will be used for all DJ downloads. The Icecast server settings (address, port, mount, password) 
              will be automatically replaced with the DJ's specific values when they download their configuration.
            </Typography>
            
            <TextField
              label="Configuration Name"
              value={configName}
              onChange={(e) => setConfigName(e.target.value)}
              fullWidth
              required
              margin="normal"
              variant="outlined"
            />
            
            <TextField
              label="BUTT Configuration Template"
              multiline
              rows={20}
              value={config || ''}
              onChange={(e) => setConfig(e.target.value)}
              fullWidth
              required
              margin="normal"
              variant="outlined"
              sx={{ fontFamily: 'monospace' }}
            />
          </Paper>
        )}

        {showHistory && (
          <Paper elevation={3} sx={{ p: 3 }}>
            <Typography variant="h6" component="h2" gutterBottom>
              Configuration History
            </Typography>
            
            {historyLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', my: 3 }}>
                <CircularProgress />
              </Box>
            ) : configHistory.length === 0 ? (
              <Typography variant="body1">No configuration history found.</Typography>
            ) : (
              configHistory.map((version, index) => (
                <Accordion key={version._id}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography sx={{ fontWeight: version.isActive ? 'bold' : 'normal' }}>
                      {version.name} {version.isActive && '(Active)'} - {new Date(version.updatedAt).toLocaleString()}
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        Created by: {version.updatedBy?.name || 'Unknown'}
                      </Typography>
                      <Typography variant="body2">
                        Last updated: {new Date(version.updatedAt).toLocaleString()}
                      </Typography>
                    </Box>
                    <Button 
                      variant="outlined" 
                      onClick={() => handleLoadVersion(version)}
                      sx={{ mb: 2 }}
                    >
                      Load This Version
                    </Button>
                    <Divider sx={{ mb: 2 }} />
                    <TextField
                      multiline
                      rows={10}
                      value={version.mainConfig}
                      fullWidth
                      InputProps={{ readOnly: true }}
                      sx={{ fontFamily: 'monospace' }}
                    />
                  </AccordionDetails>
                </Accordion>
              ))
            )}
          </Paper>
        )}
      </Box>

      {/* New Version Dialog */}
      <Dialog open={openNewVersionDialog} onClose={handleCloseNewVersionDialog}>
        <DialogTitle>Create New Configuration Version</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            This will create a new version of the BUTT configuration template and set it as the active version. Previous versions will be preserved.
          </DialogContentText>
          <TextField
            autoFocus
            margin="dense"
            label="Version Name"
            fullWidth
            variant="outlined"
            value={newVersionName}
            onChange={(e) => setNewVersionName(e.target.value)}
            required
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseNewVersionDialog}>Cancel</Button>
          <Button 
            onClick={handleCreateNewVersion} 
            variant="contained"
            disabled={!newVersionName.trim() || saving}
          >
            {saving ? <CircularProgress size={24} /> : 'Create Version'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default AdminButtConfig; 